import React, { useState } from 'react';
import { TouchableWithoutFeedback } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronRightIcon,
    Icon,
    isNonMobileWeb,
    ScreenContainer,
    XStack,
    YStack,
} from 'smoothie';

import {
    isPartialBillValid,
    mapPartialBillToBill,
} from '../../../common/utils/billUtils';
import { captureException } from '../../../logger/Logger';
import type { IBill, PartialBill } from '../../types/Bill';
import {
    BillAccountRow,
    BillCadenceRow,
    BillCategoryRow,
    BillDisplayNameRow,
    BillPayeeRow,
    BillTimingRow,
} from '../common/BillRows';
import BillAmountEditor, { EditableAmountField } from './BillAmountEditor';
import DetailsEditorSheet, { BillDetailIndex } from './BillDetailsEditorSheet';

const EditableDetailField = ({
    rowComponent,
    onPress,
}: {
    rowComponent: React.JSX.Element;
    onPress: () => void;
}) => (
    <TouchableWithoutFeedback onPress={onPress}>
        <XStack
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            paddingVertical={8}
            gap={12}
            cursor="pointer"
        >
            {rowComponent}
            <Icon icon={ChevronRightIcon} size={20} />
        </XStack>
    </TouchableWithoutFeedback>
);

const DetailsDisplaySection = ({
    bill,
    submitButtonLabel,
    onPressAmountField,
    onPressDetailField,
    onSubmit,
}: {
    bill: PartialBill;
    submitButtonLabel: string;
    onPressAmountField: () => void;
    onPressDetailField: (index: BillDetailIndex) => void;
    onSubmit: (bill: IBill) => void;
}) => {
    const isBillValid = isPartialBillValid(bill);

    return (
        <YStack
            paddingTop={20}
            flex={1}
            height="100%"
            justifyContent={isNonMobileWeb ? 'flex-start' : 'space-between'}
            gap={isNonMobileWeb ? 32 : 16}
        >
            <YStack
                paddingTop={24}
                paddingBottom={48}
                alignItems="center"
                justifyContent="center"
            >
                <EditableAmountField bill={bill} onPress={onPressAmountField} />
            </YStack>
            <YStack>
                <BodyMediumRegular>Details</BodyMediumRegular>
                <YStack marginVertical={10} gap={16}>
                    <YStack>
                        <EditableDetailField
                            rowComponent={<BillDisplayNameRow bill={bill} />}
                            onPress={() => {
                                onPressDetailField(
                                    BillDetailIndex.DISPLAY_NAME,
                                );
                            }}
                        />
                        <EditableDetailField
                            rowComponent={
                                <BillPayeeRow
                                    bill={bill}
                                    useGenericIcon={true}
                                />
                            }
                            onPress={() => {
                                onPressDetailField(BillDetailIndex.PAYEE);
                            }}
                        />
                        <EditableDetailField
                            rowComponent={
                                <BillAccountRow
                                    accountId={bill.accountId ?? null}
                                    viewType="bill_details"
                                />
                            }
                            onPress={() => {
                                onPressDetailField(BillDetailIndex.ACCOUNT);
                            }}
                        />
                        <EditableDetailField
                            rowComponent={
                                <BillCadenceRow cadence={bill.cadence} />
                            }
                            onPress={() => {
                                onPressDetailField(BillDetailIndex.CADENCE);
                            }}
                        />
                        <EditableDetailField
                            rowComponent={
                                <BillTimingRow
                                    timingDescription={bill.timingDescription}
                                />
                            }
                            onPress={() => {
                                onPressDetailField(BillDetailIndex.TIMING);
                            }}
                        />
                        <EditableDetailField
                            rowComponent={
                                <BillCategoryRow
                                    categoryDescription={bill.category}
                                />
                            }
                            onPress={() => {
                                onPressDetailField(BillDetailIndex.CATEGORY);
                            }}
                        />
                    </YStack>
                </YStack>
            </YStack>
            <Button
                variant={ButtonVariant.PRIMARY}
                tone="positive"
                size={ButtonSize.LARGE}
                text={submitButtonLabel}
                disabled={!isBillValid}
                onPress={() => {
                    // TODO field validation - must have amount, accountId, cadence, name, payee
                    try {
                        const billToSubmit = mapPartialBillToBill(bill);
                        onSubmit(billToSubmit);
                    } catch (e) {
                        // TODO handle error
                        captureException(e);
                    }
                }}
            />
        </YStack>
    );
};

interface BillDetailsEditorProps {
    bill: PartialBill;
    title: string;
    submitButtonLabel: string;
    onSubmit: (bill: IBill) => void;
    onBack: () => void;
}

const BillDetailsEditor = ({
    bill = {},
    title,
    submitButtonLabel,
    onSubmit,
    onBack,
}: BillDetailsEditorProps) => {
    const [billState, setBillState] = useState<PartialBill>(bill);
    const [showAmountEditor, setShowAmountEditor] = useState(false);
    const [detailsEditorSheetIsOpen, setDetailsEditorSheetIsOpen] =
        useState(false);
    const [currentBillDetailIndex, setCurrentBillDetailIndex] =
        useState<BillDetailIndex>(BillDetailIndex.DISPLAY_NAME);

    return (
        <ScreenContainer
            title={title}
            hasBackButton={true}
            onBack={onBack}
            onFallbackRoute="/bills/bills-overview"
        >
            {showAmountEditor ? (
                <BillAmountEditor
                    bill={billState}
                    onSubmit={(updatedBill: PartialBill) => {
                        setBillState((currentBill) => {
                            setShowAmountEditor(false);
                            setDetailsEditorSheetIsOpen(false);
                            return {
                                ...currentBill,
                                ...updatedBill,
                            };
                        });
                    }}
                />
            ) : (
                <DetailsDisplaySection
                    bill={billState}
                    submitButtonLabel={submitButtonLabel}
                    onPressAmountField={() => {
                        setShowAmountEditor(true);
                    }}
                    onPressDetailField={(index: BillDetailIndex) => {
                        setCurrentBillDetailIndex(index);
                        setDetailsEditorSheetIsOpen(true);
                    }}
                    onSubmit={onSubmit}
                />
            )}
            <DetailsEditorSheet
                bill={billState}
                currentBillDetailIndex={currentBillDetailIndex}
                isOpen={detailsEditorSheetIsOpen}
                onSubmit={(updatedBill: PartialBill) => {
                    setBillState((currentBill) => {
                        return {
                            ...currentBill,
                            ...updatedBill,
                        };
                    });
                }}
                onClose={() => {
                    setDetailsEditorSheetIsOpen(false);
                }}
            />
        </ScreenContainer>
    );
};

export default BillDetailsEditor;
